import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getDesignTokens } from '../../base/core/color';
import { spc } from '../../base/spacer';
import { useDashboardDataContext } from '../../project/dashboard/dashboard-context';

import { lineColor } from '~/client/components/assessment/risk-over-time/risk-over-time-graph';
import { RootState, actions } from '~/client/redux';
import { AnswerType } from '~/graphql/generated/client';
import * as GQL from '~/graphql/generated/client';
import { LeftNavMenu } from '~c/components/base/left-nav';
import Switch from '~c/components/base/switch';
import {
  aiSystemBundleRouteUtil,
  candidateAgentBundleRouteUtil,
  candidateModelBundleRouteUtil,
  functionalModelBundleRouteUtil,
} from '~c/routes';

interface Props {
  className?: string;
}
type CheckedState = {
  [key: string]: boolean;
};

const RiskOverTimeLeftNavImpl: React.FC<Props> = ({ className }) => {
  const { assessmentId, bundleId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedControlId = queryParams.get('id');

  const { project } = useDashboardDataContext();

  const { projectId, candidateId, functionalModelId, agentId } = useParams();
  const effectiveProjectId =
    candidateId ?? agentId ?? functionalModelId ?? projectId;

  const routeUtil = candidateId
    ? candidateModelBundleRouteUtil
    : agentId
      ? candidateAgentBundleRouteUtil
      : functionalModelId
        ? functionalModelBundleRouteUtil
        : aiSystemBundleRouteUtil;

  const { matchRoute, navigateTo } = routeUtil.useHelper();

  const [checkedState, setCheckedState] = React.useState<CheckedState>({});
  const dispatch = useDispatch();

  const prevSelectedContr = useSelector(
    (state: RootState) => state.riskOverTime.selectedControls
  );
  const allControlData = useSelector(
    (state: RootState) => state.riskOverTime.allControlData
  );

  const onClickHandler = (control: GQL.Control) => {
    setCheckedState((prevState) => ({
      ...prevState,
      [control.identifier]: !prevState[control.identifier],
    }));
    const { identifier } = control;
    const selectedControl = allControlData.find((e) => e.id === identifier);

    if (selectedControl) {
      const isExist = prevSelectedContr.find((e) => e.id === identifier);
      if (isExist) {
        return dispatch(
          actions.riskOverTime.setSelectedControls(
            prevSelectedContr.filter((e) => e.id !== identifier)
          )
        );
      }
      const data = [...prevSelectedContr, selectedControl];
      return dispatch(actions.riskOverTime.setSelectedControls(data));
    }
  };

  const assessment_ = project?.assessments.find(
    (assess) => assess._id === assessmentId
  );

  const controls =
    assessment_?.policy?.controlBundleList
      ?.find((c) => c._id === bundleId)
      ?.controls.filter((c) => c.answerType === AnswerType.TestScore) ?? [];

  React.useEffect(() => {
    if (allControlData) {
      const initialCheckedState: CheckedState = {};
      initialCheckedState[selectedControlId ?? ''] = true;
      setCheckedState(initialCheckedState);
    }
  }, [allControlData]);

  return (
    <LeftNavMenu
      className={className}
      name={matchRoute('RISK_OVER_TIME') ? routeUtil.routePrefix : ''}
      header={{
        showBackButton: true,
        onClick: () => {
          dispatch(actions.riskOverTime.setSelectedControls([]));
          navigateTo('BUNDLE_FORM', {
            params: {
              projectId: effectiveProjectId ?? '',
              ...(functionalModelId && { functionalModelId }),
              ...(candidateId && { candidateId }),
              assessmentId: assessmentId ?? '',
              bundleId: bundleId ?? '',
            },
          });
        },
      }}
    >
      {controls?.map((control, index) => (
        <Switch
          key={control._id}
          className="switch-item"
          color={lineColor[(index % lineColor.length) + 1]}
          label={control.name}
          checked={checkedState[control.identifier] || false}
          onChange={() => onClickHandler(control)}
        />
      ))}
    </LeftNavMenu>
  );
};

const RiskOverTimeLeftNav = muiStyled(RiskOverTimeLeftNavImpl)(({ theme }) => {
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette
  );
  return css`
    .grow {
      flex-grow: 1;
    }

    .switch-item {
      padding-left: ${spc.xs}px;
      margin-bottom: ${spc.between_vertical_s}px;

      &.MuiFormControlLabel-root .MuiFormControlLabel-label {
        color: ${t.nav.itemTextOnDark};
      }
    }
  `;
});

export default RiskOverTimeLeftNav;
